<template>
    <div>
        <v-row v-show="stepper !== 2" align="center" :class="{'px-8': !isMobile, 'px-2': isMobile}">
            <v-col cols="12" md="8">
                <scheduler-filters v-model="filters" @reset-filters="resetFilters" @submit-filters="loadGroups" />
            </v-col>
            <v-col cols="12" md="4">
                <events-calendar
                    v-model="groups"
                    :class="{'ms-auto': !isMobile, 'mx-auto': isMobile}"
                    :groups="groups"
                    :loading="loading"
                    @on-page-change="onCalendarPageChange"
                    @on-day-click="onDayClick"
                />
            </v-col>
        </v-row>
        <v-stepper v-model="stepper" elevation="0" :items="[1, 2]" hide-actions>
            <v-stepper-header />
            <template v-slot:item.1>
                <div class="d-flex align-center">
                    <span v-if="groups" class="font-weight-medium text-caption me-1">
                        {{ `${$t('total_routes')}: ${groups.length}` }}
                    </span>
                    <v-btn color="primary" icon="mdi-file-excel" variant="text" @click="downloadExcel" />
                </div>
                <groups-list-table
                    ref="groupsListTable"
                    v-model="selectedGroup"
                    :group-items="groupItems"
                    :details-loading="detailsLoading"
                    @show-details="showDetails"
                />
            </template>
            <template v-slot:item.2>
                <v-btn color="primary" variant="text" @click="nextStep">{{ $t('back') }}</v-btn>
                <v-card v-if="!detailsLoading" elevation="0">
                    <v-card-title>
                        <group-details-info
                            v-if="selectedGroupDetails"
                            v-model="selectedGroupDetails"
                            :group-id="selectedGroup.groupId"
                            :is-show-seats="isShowSeats"
                        />
                    </v-card-title>
                    <v-card-text :class="{'px-0': isMobile}">
                        <group-details-table
                            v-model="selectedGroupDetails"
                            :selected-group="selectedGroup"
                            :city="city"
                            :is-show-seats="isShowSeats"
                        />
                    </v-card-text>
                </v-card>
                <v-card v-if="detailsLoading" elevation="0">
                    <v-card-text>
                        {{ $t('loading') }}
                        <v-progress-linear indeterminate color="primary" class="mb-0" />
                    </v-card-text>
                </v-card>
            </template>
        </v-stepper>
    </div>
</template>

<script lang="ts">
    import {Component, Vue} from 'vue-facing-decorator'
    import EventsCalendar from '@/components/scheduler/EventsCalendar.vue'
    import GroupsListTable from '@/components/scheduler/GroupsListTable.vue'
    import GroupDetailsInfo from '@/components/scheduler/GroupDetailsInfo.vue'
    import GroupDetailsTable from '@/components/scheduler/GroupDetailsTable.vue'
    import SchedulerFilters from '@/components/scheduler/SchedulerFilters.vue'
    import PrivateClientAndCompaniesAutocomplete from '~/components/search/forms/PrivateClientAndCompaniesAutocomplete.vue'
    import SupplierSelectAutocomplete from '~/components/search/forms/SupplierSelectAutocomplete.vue'
    import OrderServiceDaterange from '~/components/account/filters/OrderServiceDaterange.vue'
    import {downloadData} from '~/utils/helpers'

    @Component({
        components: {
            OrderServiceDaterange,
            SupplierSelectAutocomplete,
            PrivateClientAndCompaniesAutocomplete,
            GroupDetailsTable,
            GroupDetailsInfo,
            GroupsListTable,
            EventsCalendar,
            SchedulerFilters,
        },
    })
    export class SchedulerPage extends Vue {
        groups = []
        filters = {}
        loading = false

        calendarDateStart = null
        calendarDateEnd = null

        selectedGroup = null
        selectedGroupDetails = null
        detailsLoading = false

        city = false
        stepper = 1
        isShowSeats = false
        contract = {}

        async onCalendarPageChange({startDate, endDate}) {
            if (startDate && endDate) {
                this.calendarDateStart = startDate
                this.calendarDateEnd = endDate
                this.filters = {...this.filters, dateStart: startDate, dateEnd: endDate}
            }

            await this.loadGroups()
        }

        async loadGroups() {
            try {
                this.groups = []
                this.loading = true

                this.groups = (
                    await this.$api.schedulerGroups.get({
                        ...this.requestBodyGroups,
                    })
                ).schedulerGroups
            } catch {
                this.$toast.error(this.$t('error_message.load_error'))
            } finally {
                this.loading = false
            }
        }

        nextStep() {
            this.stepper = this.stepper === 1 ? 2 : 1
        }

        showDetails() {
            this.loadDetails()
            this.nextStep()
        }

        get groupItems() {
            return this.groups
                ?.map(e => {
                    return {
                        ...e,
                        time: e.dateTime.split(' ')[1],
                        date: e.dateTime.split(' ')[0],
                        service: {
                            serviceName: e.serviceName,
                            serviceType: e.serviceType,
                            country: e.country,
                            city: e.city,
                        },
                        statusColor: runtimeStore.orderStatusColor(e.status),
                    }
                })
                .sort((a, b) => this.$dateFns.parseISO(a.date) - this.$dateFns.parseISO(b.date))
        }

        get isTO1() {
            return authStore.isTO1
        }

        get personRole() {
            return authStore.personRole
        }

        async loadDetails(groupId = null) {
            try {
                this.selectedGroupDetails = null
                this.detailsLoading = true
                this.selectedGuests = []

                let driver
                let guide

                const rs = await this.$api.schedulerGroupDetails.get({
                    groupId: groupId || this.selectedGroup.groupId,
                })

                await Promise.all([
                    (async () => {
                        this.city = (
                            await this.$api.locations.get({pattern: this.selectedGroup.city, limitCities: 1})
                        ).cities[0]
                    })(),
                    (async () => {
                        driver = rs?.groupDetails.driverId
                            ? (await this.$api.persons.get({id: rs.groupDetails.driverId})).users[0]
                            : null
                    })(),
                    (async () => {
                        guide = rs?.groupDetails.guideId
                            ? (await this.$api.persons.get({id: rs.groupDetails.guideId})).users[0]
                            : null
                    })(),
                ])

                this.selectedGroupDetails = {
                    ...rs.groupDetails,
                    guide,
                    driver,
                }

                if (this.isTO1) {
                    if (
                        this.selectedGroupDetails?.serviceType === 'EXCURSION' &&
                        runtimeStore.getCRMOption('asp.option.ownExcursions.show.seats')
                    ) {
                        await this.loadContract()
                        this.isShowSeats = this.contract?.transportationType === 'OFF_SITE'
                    }
                    if (
                        this.selectedGroupDetails?.serviceType === 'TRANSFER' &&
                        runtimeStore.getCRMOption('asp.option.ownTransfers.show.seats')
                    ) {
                        this.isShowSeats = true
                    }
                }
                // eslint-disable-next-line no-empty
            } catch (e) {
            } finally {
                this.detailsLoading = false
            }
        }

        async loadContract() {
            try {
                const {ownProductContracts} = await this.$api.ownExcursionContracts.get({
                    id: this.selectedGroupDetails.productId,
                })
                this.contract = ownProductContracts[0]
            } catch (e) {
                this.contract = {}
            }
        }

        async downloadExcel() {
            this.loading = true
            try {
                const data = await this.$api.downloadSchedulerGroups.get({
                    fileType: 'EXCEL',
                    ...this.filters,
                })

                await downloadData(data, 'schedulerGroups.xlsx')
            } catch {
                this.$toast.error(this.$t('error_message.load_error'))
            }
            this.loading = false
        }

        get isMobile() {
            return this.$breakpoint.smAndDown
        }

        async resetFilters() {
            await this.loadGroups()
        }

        get requestBodyGroups() {
            return {
                ...(this.isDriver && {driverId: this.personId}),
                ...(this.isGuide && {guideId: this.personId}),
                ...this.filters,
            }
        }

        onDayClick(e) {
            this.$refs.groupsListTable?.$refs?.[e.id]?.scrollIntoView({behavior: 'smooth'})
        }

        get isGuide() {
            return authStore.isGuide
        }

        get isDriver() {
            return authStore.isDriver
        }

        get personId() {
            return authStore.person.personId
        }
    }

    export default SchedulerPage
</script>

<style scoped lang="scss">
    :deep(div.v-expansion-panel-content__wrap) {
        padding: 0 !important;
    }
    :deep(.v-stepper-header) {
        display: none;
    }
</style>
